import { useEffect, useState } from "react";
import axios from "axios";

const ActivationCodesList = () => {
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [codesLoading, setCodesLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/codes/batches`,
      );

      if (response.data.success) {
        setBatches(response.data.batches || []);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch batches");
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = async (batchName) => {
    try {
      setCodesLoading(true);
      setSelectedBatch(batchName);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/codes/batch/${batchName}`,
      );

      if (response.data.success) {
        setCodes(response.data.codes || []);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch codes");
    } finally {
      setCodesLoading(false);
    }
  };
  const deleteBatch = async (batchName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the batch "${batchName}" and all its activation codes?`,
    );

    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/codes/batch/${batchName}`,
      );

      alert("Batch deleted successfully");

      // Clear codes panel if currently viewing deleted batch
      if (selectedBatch === batchName) {
        setSelectedBatch(null);
        setCodes([]);
      }

      // Refresh batches list
      fetchBatches();
    } catch (error) {
      alert(error.response?.data?.message || "Failed to delete batch");
    }
  };

  const downloadExcel = async (batchName) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/codes/batch/${batchName}/download`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute("download", `${batchName}-activation-codes.xlsx`);

      document.body.appendChild(link);

      link.click();

      link.remove();
    } catch (error) {
      alert("Failed to download excel");
    }
  };
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedCodes = [...codes].sort((a, b) => {
    if (!sortField) return 0;

    let valueA;
    let valueB;

    if (sortField === "status") {
      valueA = a.is_used ? 1 : 0;
      valueB = b.is_used ? 1 : 0;
    } else if (sortField === "used_at") {
      valueA = a.used_at ? new Date(a.used_at).getTime() : 0;
      valueB = b.used_at ? new Date(b.used_at).getTime() : 0;
    } else if (sortField === "expires_at") {
      valueA = a.expires_at ? new Date(a.expires_at).getTime() : 0;
      valueB = b.expires_at ? new Date(b.expires_at).getTime() : 0;
    }

    if (valueA < valueB) {
      return sortDirection === "asc" ? -1 : 1;
    }

    if (valueA > valueB) {
      return sortDirection === "asc" ? 1 : -1;
    }

    return 0;
  });

  return (
    <div className="container">
      <h3 className="mb-4">Activation Code Batches</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Batch List */}
      <div className="card mb-4">
        <div className="card-header">All Batches</div>

        <div className="card-body">
          {loading ? (
            <p>Loading batches...</p>
          ) : batches.length === 0 ? (
            <p>No batches found</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>Batch Name</th>
                    <th>Total Codes</th>
                    <th>Used</th>
                    <th>Unused</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {batches.map((batch, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{batch.batch_name || "No Batch"}</strong>
                      </td>

                      <td>{batch.total_codes}</td>

                      <td>
                        <span className="badge bg-success">
                          {batch.used_codes}
                        </span>
                      </td>

                      <td>
                        <span className="badge bg-warning text-dark">
                          {batch.unused_codes}
                        </span>
                      </td>

                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => fetchCodes(batch.batch_name)}
                          >
                            View Codes
                          </button>

                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => downloadExcel(batch.batch_name)}
                          >
                            Download Excel
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteBatch(batch.batch_name)}
                          >
                            Delete Batch
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Codes Table */}
      {selectedBatch && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>
              Batch: <strong>{selectedBatch}</strong>
            </span>

            <button
              className="btn btn-sm btn-success"
              onClick={() => downloadExcel(selectedBatch)}
            >
              Download Excel
            </button>
          </div>
          <div className="card-body">
            {codesLoading ? (
              <p>Loading codes...</p>
            ) : codes.length === 0 ? (
              <p>No codes found</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Code</th>

                      <th
                        onClick={() => handleSort("status")}
                        style={{ cursor: "pointer" }}
                      >
                        Status{" "}
                        {sortField === "status" && (
                          <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>

                      <th>Used By</th>

                      <th
                        onClick={() => handleSort("used_at")}
                        style={{ cursor: "pointer" }}
                      >
                        Used At{" "}
                        {sortField === "used_at" && (
                          <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>

                      <th
                        onClick={() => handleSort("expires_at")}
                        style={{ cursor: "pointer" }}
                      >
                        Expiry{" "}
                        {sortField === "expires_at" && (
                          <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedCodes.map((code) => (
                      <tr key={code.id}>
                        <td>
                          <strong>{code.code}</strong>
                        </td>

                        <td>
                          {code.is_used ? (
                            <span className="badge bg-danger">Used</span>
                          ) : (
                            <span className="badge bg-success">Unused</span>
                          )}
                        </td>

                        <td>{code.user?.name || "-"}</td>

                        <td>
                          {code.used_at
                            ? new Date(code.used_at).toLocaleString()
                            : "-"}
                        </td>

                        <td>
                          {code.expires_at
                            ? new Date(code.expires_at).toLocaleString()
                            : "No Expiry"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationCodesList;
